import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import './CertificateOfRecognition.css';

const DOCUMENTATION_AREAS = [
  { text: 'Workplace health and safety policies', icon: 'shield' },
  { text: 'Risk assessments and hazard controls', icon: 'hazard' },
  { text: 'Workplace inspections', icon: 'clipboard' },
  { text: 'Incident and investigation records', icon: 'file' },
  { text: 'Worker training and inductions', icon: 'users' },
  { text: 'Corrective actions', icon: 'task' },
  { text: 'Safety procedures', icon: 'list' },
  { text: 'WHS responsibilities and records', icon: 'badge' },
  { text: 'Audit evidence and supporting documentation', icon: 'folder' },
];

const HELP_FEATURES = [
  {
    title: 'Centralise WHS Documents',
    icon: 'folder',
    paragraphs: [
      'Complimate provides a central document hub for policies, procedures, SWMS, WHS plans, registers and compliance records. Documents can include version information and review or expiry dates, helping teams find the current document when needed.',
    ],
  },
  {
    title: 'Record Inspections and Audits',
    icon: 'clipboard',
    paragraphs: [
      'Digital forms can be used for inspections, incident reports, hazard reporting, pre-start checklists and other workplace records. Forms can be accessed through QR codes and mobile devices, with completed records available for review and PDF export.',
    ],
  },
  {
    title: 'Track Corrective Actions',
    icon: 'task',
    paragraphs: [
      'When an inspection or audit identifies an issue, teams can assign tasks and corrective actions, track progress and maintain supporting information. This can help create a clearer evidence trail for internal compliance management.',
    ],
  },
  {
    title: 'Keep Compliance Records Organised',
    icon: 'archive',
    paragraphs: [
      'Instead of relying on separate spreadsheets, paper forms and email attachments, teams can bring documents, forms, sites, assets and compliance activities into one platform.',
    ],
  },
];

const FAQ_ITEMS = [
  {
    question: 'What does COR stand for?',
    answer:
      'COR commonly stands for Certificate of Recognition, but the meaning and requirements depend on the specific industry and jurisdiction.',
  },
  {
    question: 'Is Certificate of Recognition mandatory in Australia?',
    answer:
      'There is no single Australia-wide WHS requirement that makes every business obtain a Certificate of Recognition. Requirements depend on the particular certification scheme, industry and jurisdiction.',
  },
  {
    question: 'Does Complimate provide COR certification?',
    answer:
      'No. Complimate is compliance management software. It helps businesses manage WHS documents, digital forms, inspections, audits and corrective actions, but it does not issue COR certification.',
  },
  {
    question: 'Can Complimate help prepare safety records?',
    answer:
      'Yes. Complimate supports document management, digital forms, inspections, audits, corrective actions, tasks and other compliance workflows.',
  },
  {
    question: 'Can Complimate replace a WHS consultant or certification auditor?',
    answer:
      'No. Software can help organise and manage compliance activities, but it does not replace professional WHS advice, legal advice or an independent certification/audit process where one is required.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/certificate-of-recognition/');

function CheckIcon() {
  return (
    <span className="seo-check" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3.75 9.75L7.125 13.125L14.25 4.875"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const ICON_PATHS = {
  award: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="M8.5 14.5 7 21l5-2.5L17 21l-1.5-6.5" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5" />
      <path d="M12 17h.01" />
    </>
  ),
  clipboard: (
    <>
      <path d="M8 4h8" />
      <path d="M9 3v3h6V3" />
      <path d="M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  list: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </>
  ),
  badge: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="5" rx="1" />
      <path d="M5 9v10h14V9" />
      <path d="M10 13h4" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="cor-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.award}
      </svg>
    </span>
  );
}

function CertificateOfRecognition() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="cor-page">
        <section className="cor-hero">
          <Container>
            <div className="cor-hero-grid">
              <div className="cor-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="award" />
                  Certificate of Recognition (COR)
                </p>
                <h1 className="seo-h1">
                  What Is a Certificate of Recognition (COR)?
                </h1>
                <p className="seo-lead">
                  A Certificate of Recognition (COR) is a formal recognition or
                  certification used by certain industry and jurisdiction-specific
                  programs to assess an organisation&apos;s occupational health and
                  safety management system. The exact meaning, eligibility
                  requirements and certification process depend on the program and
                  jurisdiction.
                </p>
                <div className="cor-note">
                  <FeatureIcon name="info" />
                  <p className="seo-body">
                    In Australia, however, COR can refer to different certification
                    schemes depending on the industry. For example, the Australian
                    Maritime Safety Authority uses Certificate of Recognition for
                    recognising certain overseas STCW seafarer qualifications.
                  </p>
                </div>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal cor-primary-button"
                    id="cor_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>

              <div className="cor-hero-visual">
                <div className="cor-visual-glow" aria-hidden="true" />
                <div className="cor-product-window">
                  <div className="cor-window-bar" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <img
                    src={isDark ? DarkDocument : LightDocument}
                    alt="Complimate document hub showing WHS policies, procedures and compliance records"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>

                <aside className="cor-certificate-card" aria-hidden="true">
                  <div className="cor-seal">
                    <FeatureIcon name="award" />
                  </div>
                  <span className="cor-certificate-kicker">Certificate of Recognition</span>
                  <span className="cor-certificate-line cor-certificate-line-lg" />
                  <span className="cor-certificate-line" />
                  <span className="cor-certificate-line cor-certificate-line-sm" />
                  <div className="cor-certificate-checks">
                    <CheckIcon />
                    <span />
                    <CheckIcon />
                    <span />
                    <CheckIcon />
                    <span />
                  </div>
                </aside>

                <div className="cor-float-card cor-float-card-top" aria-hidden="true">
                  <FeatureIcon name="clipboard" />
                  <span>Workplace inspections</span>
                </div>
                <div className="cor-float-card cor-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="folder" />
                  <span>Audit evidence</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section cor-why-section">
          <Container>
            <div className="cor-why-grid">
              <div>
                <div className="cor-section-marker">
                  <FeatureIcon name="file" />
                </div>
                <h2 className="seo-h2">Why Is COR Documentation Important?</h2>
                <p className="seo-body">
                  Where an organisation participates in a COR or similar safety
                  certification program, maintaining clear and current safety
                  records can be an important part of demonstrating how its
                  safety management system operates.
                </p>
                <p className="seo-body">
                  Depending on the applicable program, organisations may need to
                  maintain records relating to areas such as:
                </p>
              </div>
              <div className="cor-shot-frame">
                <img
                  src={isDark ? DarkDashboard : LightDashboard}
                  alt="Complimate dashboard with tasks, documents and compliance activity overview"
                  width="960"
                  height="680"
                />
              </div>
            </div>

            <h3 className="seo-h3 cor-record-heading">Common Record Areas</h3>
            <ul className="cor-record-grid">
              {DOCUMENTATION_AREAS.map((item) => (
                <li className="cor-record-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body cor-evidence-note">
              The specific evidence required should always be checked against
              the requirements of the relevant certification program.
            </p>
          </Container>
        </section>

        <section className="seo-section cor-cta-section">
          <Container>
            <div className="cor-cta-card">
              <div className="cor-cta-orb cor-cta-orb-one" aria-hidden="true" />
              <div className="cor-cta-orb cor-cta-orb-two" aria-hidden="true" />
              <div className="cor-cta-copy">
                <h2 className="seo-h2">Manage WHS Documentation in One Place</h2>
                <p className="seo-body">
                  Use Complimate to organise WHS documents, digital forms,
                  inspections, audits and corrective actions across your business.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal cor-cta-button"
                id="cor_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section cor-help-section">
          <Container>
            <div className="cor-help-intro">
              <div className="cor-shot-frame cor-shot-frame-soft">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate document upload flow for WHS and compliance records"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <h2 className="seo-h2">
                  How Can Compliance Software Help Manage COR-Related Records?
                </h2>
                <p className="seo-body">
                  Complimate is a WHS and compliance management platform designed to
                  help businesses organise compliance documents, forms, inspections,
                  audits, tasks and corrective actions. It is not a Certificate of
                  Recognition issuing or certification body.
                </p>
                <p className="seo-body">
                  For organisations that need to maintain structured safety records,
                  Complimate can support activities such as:
                </p>
              </div>
            </div>

            <div className="cor-help-grid">
              {HELP_FEATURES.map((item, index) => (
                <article className="cor-help-card" key={item.title}>
                  <div className="cor-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 48)} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section cor-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default CertificateOfRecognition;
